import { Formik, Field, Form } from "formik";
import tw from "tailwind-styled-components";
import {
  InputGroup,
  Label,
  SubmitBtn,
  FieldWrappers,
  SideCancelBtn,
  Wrapper,
  ScrollableContent,
  ActionSection,
  CardWrapper,
  SelectionCard,
  ModalHeading,
  ModalSubHeading,
  Note,
} from "../Styles/InputStyles";
import { MdOutlineInfo } from "react-icons/md";
import * as Yup from "yup";
import Config from "../../Config";
import { furnishedOptions, preferredTenantOptions } from "../../helpers";

const PropertyInfo = ({
  stepOneData,
  step,
  setStep,
  stepTwoData,
  setStepThreeData,
  stepThreeData,
  createInventoryMutate,
  createInventoryLoading,
}) => {
  const isRent = stepOneData?.listingType === 1;
  const isSale = stepOneData?.listingType === 2;
  const isLease = stepOneData?.listingType === 3;

  const initialValues = stepThreeData || {
    furnishedType: "",
    parking: "",
    squareYards: "",
    builtUpArea: "",
    ...(isRent && {
      rent: "",
      securityDeposit: "",
      maintenanceCharges: "",
      preferredTenantType: "",
      foodPreference: "",
    }),
    ...((isSale || isLease) && {
      amount: "",
    }),

    styleType: "",
    attachedBathroom: "",
    toiletType: "",
    numberOfBathrooms: "",
    petsAllowed: "",
  };

  const foodPreferenceOptions = [
    { value: 1, label: "Veg" },
    { value: 2, label: "Non-Veg" },
    { value: 3, label: "Whatever" },
  ];

  const validationSchema = Yup.object({
    furnishedType: Yup.string().required("Please select furnished type"),
    ...(isRent && {
      rent: Yup.string()
        .required("Rent is required")
        .test(
          "rent-range",
          "Rent must be between ₹1,000 and ₹50,00,000",
          function (value) {
            if (!value) return false;
            const numValue = parseInt(value);
            return numValue >= 1000 && numValue <= 5000000;
          },
        ),
      preferredTenantType: Yup.string().required(
        "Please select preferred tenant type",
      ),
      foodPreference: Yup.string().required("Please select food preference"),

      moveInCharges: Yup.string().test(
        "movein-positive",
        "Move-In charges must be a positive number",
        function (value) {
          if (!value) return true;
          const numValue = parseInt(value);
          return numValue >= 0;
        },
      ),

      moveOutCharges: Yup.string().test(
        "moveout-positive",
        "Move-Out charges must be a positive number",
        function (value) {
          if (!value) return true;
          const numValue = parseInt(value);
          return numValue >= 0;
        },
      ),
    }),

    ...((isSale || isLease) && {
      amount: Yup.string()
        .required("Amount is required")
        .test(
          "amount-range",
          "Amount must be between ₹1,00,000 and ₹5,00,00,000",
          function (value) {
            if (!value) return false;
            const numValue = parseInt(value);
            return numValue >= 100000 && numValue <= 500000000;
          },
        ),

      styleType: Yup.string().required("Please select bathroom style"),
      attachedBathroom: Yup.string().when("styleType", {
        is: 2,
        then: (schema) => schema.required("Select attached bathroom option"),
      }),
      toiletType: Yup.string().when("styleType", {
        is: 2,
        then: (schema) => schema.required("Select toilet type"),
      }),
      numberOfBathrooms: Yup.number().when("styleType", {
        is: 2,
        then: (schema) =>
          schema
            .required("Enter number of bathrooms")
            .min(1, "Minimum 1 bathroom required"),
      }),
    }),
  });

  const handleSubmit = (values, { setSubmitting }) => {
    let formData;

    if (isRent) {
      formData = {
        inventoryType: stepTwoData?.propertyType,
        amount: parseInt(values.rent),
        deposit: values.securityDeposit ? parseInt(values.securityDeposit) : 0,
        maintenance: values.maintenanceCharges
          ? parseInt(values.maintenanceCharges)
          : 0,
        moveInCharges: values.moveInCharges
          ? parseInt(values.moveInCharges)
          : 0,
        moveOutCharges: values.moveOutCharges
          ? parseInt(values.moveOutCharges)
          : 0,
        tenantPreference: parseInt(values.preferredTenantType),
        foodPreference: parseInt(values.foodPreference),
        furnishingType: parseInt(values.furnishedType),
        parking: parseInt(values.parking),
        squareYards: parseInt(values.squareYards) || 0,
        builtUpArea: parseInt(values.builtUpArea) || 0,
      };
    } else if (isSale || isLease) {
      formData = {
        propertyType: stepTwoData?.propertyType,
        amount: parseInt(values.amount),
        squareYards: parseInt(values.squareYards) || 0,
        builtUpArea: parseInt(values.builtUpArea) || 0,
        furnishedType: parseInt(values.furnishedType),
        parking: parseInt(values.parking),
      };
    }

    let cleanedStepOne = { ...stepOneData };

    if (
      cleanedStepOne.belongsToType === Config.USER_TYPE_RETAIL.CHANNEL_PARTNER
    ) {
      delete cleanedStepOne.ownerName;
      delete cleanedStepOne.ownerId;
    } else {
      delete cleanedStepOne.brokerName;
      delete cleanedStepOne.brokerId;
    }

    const completeData = {
      ...cleanedStepOne,
      ...stepTwoData,
      ...formData,
    };
    createInventoryMutate(completeData);
  };
  const handleGoBack = (values) => {
    setStepThreeData(values);
    setStep(step - 1);
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
      validateOnBlur={true}
      validateOnChange={true}
    >
      {({ values, setFieldValue, errors, touched, isSubmitting }) => (
        <Form>
          <ModalSubHeading>Step 3</ModalSubHeading>
          <ModalHeading>Property Info</ModalHeading>

          <ScrollableContent>
            <Wrapper>
              {/* Rent */}
              {isRent && (
                <>
                  <InputGroup>
                    <Label htmlFor="rent">Monthly Rent (₹) *</Label>
                    <FieldWrappers>
                      <Field
                        type="number"
                        name="rent"
                        id="rent"
                        autoComplete="off"
                        placeholder="Enter monthly rent"
                        min="1000"
                        max="5000000"
                        className=""
                      />
                    </FieldWrappers>
                    {touched.rent && errors.rent && (
                      <p className="text-rose-600 text-sm mt-1">
                        {errors.rent}
                      </p>
                    )}
                  </InputGroup>

                  <CombineInputGroup>
                    <InputGroup>
                      <Label htmlFor="securityDeposit">
                        Security Deposit (₹)
                      </Label>
                      <FieldWrappers>
                        <Field
                          type="number"
                          name="securityDeposit"
                          id="securityDeposit"
                          autoComplete="off"
                          placeholder="Enter security deposit"
                          min="1"
                          max="1000000"
                        />
                      </FieldWrappers>
                      {touched.securityDeposit && errors.securityDeposit && (
                        <p className="text-rose-600 text-sm mt-1">
                          {errors.securityDeposit}
                        </p>
                      )}
                    </InputGroup>

                    <InputGroup>
                      <Label htmlFor="maintenanceMoCharges">
                        Maintenance Charges (₹)
                      </Label>
                      <FieldWrappers>
                        <Field
                          type="number"
                          name="maintenanceCharges"
                          id="maintenanceCharges"
                          autoComplete="off"
                          placeholder="Enter maintenance charges"
                          min="0"
                        />
                      </FieldWrappers>
                      {touched.maintenanceCharges &&
                        errors.maintenanceCharges && (
                          <p className="text-rose-600 text-sm mt-1">
                            {errors.maintenanceCharges}
                          </p>
                        )}
                    </InputGroup>
                  </CombineInputGroup>

                  {/* Furnished Type */}
                  <InputGroup>
                    <Label>Furnished Type *</Label>
                    <CardWrapper>
                      {furnishedOptions.map((option) => (
                        <SelectionCard
                          key={option.value}
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            setFieldValue("furnishedType", option.value);
                          }}
                          className={
                            values.furnishedType === option.value
                              ? "bg-[#26A69A] text-white"
                              : "bg-gray-50 text-gray-800"
                          }
                        >
                          <span className="text-sm">{option.label}</span>
                        </SelectionCard>
                      ))}
                    </CardWrapper>
                    {touched.furnishedType && errors.furnishedType && (
                      <p className="text-rose-600 text-sm mt-1">
                        {errors.furnishedType}
                      </p>
                    )}
                  </InputGroup>
                  <InputGroup>
                    <Label>Preferred Tenant Type *</Label>
                    <CardWrapper>
                      {preferredTenantOptions.map((option) => (
                        <SelectionCard
                          key={option.value}
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            setFieldValue("preferredTenantType", option.value);
                          }}
                          className={
                            values.preferredTenantType === option.value
                              ? "bg-[#26A69A] text-white"
                              : "bg-gray-50 text-gray-800"
                          }
                        >
                          <span className="text-sm">{option.label}</span>
                        </SelectionCard>
                      ))}
                    </CardWrapper>
                    {touched.preferredTenantType &&
                      errors.preferredTenantType && (
                        <p className="text-rose-600 text-sm mt-1">
                          {errors.preferredTenantType}
                        </p>
                      )}
                  </InputGroup>

                  <InputGroup>
                    <Label>Food Preference *</Label>
                    <CardWrapper>
                      {foodPreferenceOptions.map((option) => (
                        <SelectionCard
                          key={option.value}
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            setFieldValue("foodPreference", option.value);
                          }}
                          className={
                            values.foodPreference === option.value
                              ? "bg-[#26A69A] text-white"
                              : "bg-gray-50 text-gray-800"
                          }
                        >
                          <span className="text-sm">{option.label}</span>
                        </SelectionCard>
                      ))}
                    </CardWrapper>
                    {touched.foodPreference && errors.foodPreference && (
                      <p className="text-rose-600 text-sm mt-1">
                        {errors.foodPreference}
                      </p>
                    )}
                  </InputGroup>
                </>
              )}

              {(isSale || isLease) && (
                <>
                  {/* Amount - for Flat/Apartment */}
                  <InputGroup>
                    <Label htmlFor="amount">
                      {isSale ? "Selling Amount (₹) *" : "Lease Amount (₹) *"}
                    </Label>
                    <FieldWrappers>
                      <Field
                        type="number"
                        name="amount"
                        id="amount"
                        autoComplete="off"
                        placeholder="Enter total amount"
                        min="100000"
                        max="500000000"
                      />
                    </FieldWrappers>
                    {touched.amount && errors.amount && (
                      <p className="text-rose-600 text-sm mt-1">
                        {errors.amount}
                      </p>
                    )}
                    <Note>
                      <MdOutlineInfo className="mt-0.5 mr-1" />
                      Minimum: ₹1,00,000 | Maximum: ₹5,00,00,000
                    </Note>
                  </InputGroup>
                </>
              )}
              {/* Square Yards - for Flat/Apartment */}
            </Wrapper>
          </ScrollableContent>

          <ActionSection>
            <SideCancelBtn type="button" onClick={()=> handleGoBack(values)}>
              Back
            </SideCancelBtn>
            <SubmitBtn type="submit" disabled={createInventoryLoading}>
              {createInventoryLoading ? "Saving..." : "Submit"}
            </SubmitBtn>
          </ActionSection>
        </Form>
      )}
    </Formik>
  );
};

export default PropertyInfo;

const CombineInputGroup = tw.div`flex space-x-7`;
